// schoolbell-e-web(동의 화면)과 schoolbell-e-developer-web(OAuth 클라이언트 신청 폼)이 같은
// 스코프 사람이 읽는 이름을 공유해야 drift가 생기지 않는다 — 두 앱이 물리적으로 분리된 뒤에도
// (2026-08-05 developer-web-domain-separation 플랜) 유지하기 위해 공용 lib으로 옮겼다.
// 네이밍 컨벤션(2026-08-21 정리): scope key suffix가 그대로 동사를 결정한다 —
// `.read` → "읽기", `.write` → "쓰기", `.send` → "발송"(고유 동작이라 예외). `.admin`(2026-08-24
// 신설, org.teams.admin/org.members.admin/org.member-labels.admin) → "관리" — write보다
// 강한 전권(타 App·세션이 만든 자원까지 격리 없이 다룰 수 있음)이라 "쓰기"와 구분되는 별도
// 표현을 쓴다.
// org.* 는 원래 전부 "단체" 프리픽스로 위임 성격을 표시했다. me.orgs.write("새 단체 개설")만
// 예외 — 기존 자원을 고치는 게 아니라 새로 만드는 동작이라 "쓰기"로 뭉치면 의미가 흐려진다.
// **2026-09-15부터 이 원칙에 추가 예외가 생겼다**: org.members.*/org.member-labels.*는
// "단체 구성원" → "학교 구성원"으로(사용자 결정 — schoolbell-e는 학교 전용 플랫폼이라 더
// 구체적인 단어를 선호), org.team-non-admin-members.*/org.team-members.*(신규)는 "단체"
// 프리픽스 자체를 안 쓰고 "학급 소속 ..."으로 짓는다 — 새 org.* 라벨을 추가할 때 이 표를
// 무조건 따르지 말고, 그 스코프가 실제로 가리키는 대상(단체 전체 vs 학급 하위)에 맞는
// 단어를 우선 고른다.
// me.profile.read가 전화번호까지 포함하는 건 me-profile.controller.ts#getProfile이
// me.phone.read 분리 없이 phone/email/name을 한 번에 반환하기 때문(설계 의도와 어긋나는
// 기존 동작 — 별도 확인 필요, 이 파일은 실제 응답 그대로를 라벨링한다).
export const OAUTH_SCOPE_LABELS: Record<string, string> = {
  'org.read': '단체 기본 정보 읽기',
  'org.teams.read': '단체 내 팀(반·학급) 구조 읽기',
  'org.teams.write': '단체 내 팀(반·학급) 구조 쓰기',
  'org.teams.admin': '단체 내 팀(반·학급) 구조 관리',
  // org.team-non-admin-members/org.team-members(2026-09-15 team-admin-oauth-write 플랜) —
  // 포함관계(SCOPE_IMPLIES): team-members가 team-non-admin-members를 포함한다. 이름 그대로
  // "team-members"는 그 팀의 사람 전체(학생·학부모+담임/팀관리자)를, "team-non-admin-members"는
  // 그중 담임/팀관리자를 뺀 좁은 부분집합(학생·학부모)을 가리킨다.
  'org.team-non-admin-members.write': '학급 소속 학생·학부모 명단 쓰기',
  'org.team-non-admin-members.admin': '학급 소속 학생·학부모 명단 관리',
  'org.team-members.write': '학급 소속 구성원(담임 지정 포함) 쓰기',
  'org.team-members.admin': '학급 소속 구성원(담임 지정 포함) 관리',
  'org.members.read': '학교 구성원 명단 읽기',
  'org.members.write': '학교 구성원 정보 쓰기',
  'org.members.admin': '학교 구성원 정보 관리',
  'org.member-labels.read': '학교 구성원 라벨 읽기',
  'org.member-labels.write': '학교 구성원 라벨 쓰기',
  'org.member-labels.admin': '학교 구성원 라벨 관리',
  'org.letter.target-members.read': '단체 가정통신문 수신 대상자 읽기',
  'org.notifications.write': '단체 알림 발송',
  'org.sms.send': '단체 문자 발송',
  'org.timetable.read': '단체 시간표 읽기',
  'org.timetable.write': '단체 시간표 쓰기',
  'me.orgs.read': '소속 단체 목록 읽기',
  'me.orgs.write': '새 단체 개설',
  // 2026-09-22 범위 확정(oidc-scopes.constants.ts 참고) — 응답이 그 단체의 전체 명단이 아니라
  // 호출자 본인의 자리로만 좁혀진다. 레거시 sbe-api-v5 마이그레이션 시점(2026-07-10)엔 실
  // 게이트가 없어 "전체 명단"으로 잘못 읽히는 라벨이었다.
  'me.org.members.read': '내가 속한 단체에서 내 자신의 자리 정보 읽기',
  'me.profile.read': '이름·이메일·전화번호 읽기',
  'me.phone.read': '전화번호 읽기',
  // 서비스 자격 스코프(`client.`) — 사용자 동의 화면에는 나타나지 않고 App 심사·허용 목록에서만 보인다.
  'client.authenticator.request': '서비스 서버가 전화번호로 학교종이 앱 본인 승인 요청 보내기',
};

export function scopeLabel(scope: string): string {
  return OAUTH_SCOPE_LABELS[scope] ?? scope;
}
